import * as React from 'react';
import { useState, useEffect } from 'react';
import { useQuery, Query, Direction, Filter, Order, Condition } from './Query';
import { FilterHeader } from './QueryTable';
import Modal, { ModalSize } from '../misc/components/modal';
import Select from 'react-select';

export interface Option {
    label: any;
    value: any
}

interface MatchSortHeaderProps {
    title: string;
    field: string;
    options: Option[];
    sortField?: string;
    filterable?: boolean;
    automationId?: string;
    relatedFilter?: Filter;
    sortEnabled?: boolean;
}

export const MatchSortHeader = ({ title, field, options, sortField = null, automationId = null, relatedFilter = null, sortEnabled = true }: MatchSortHeaderProps) => {

    const [display, setDisplay] = useState(false);
    const { filters, ordering, applyOrdering } = useQuery();
    const [ordered, setOrdered] = useState<Direction>();
    const [filtered, setFiltered] = useState(false);
    sortField = sortField ?? field;

    useEffect(() => {
        const filtered = filters.some(f => f.field === field && f.condition === 'eq');
        setFiltered(filtered);
    }, [filters]);

    useEffect(() => {
        const ordered = ordering.find(o => o.field === sortField);
        setOrdered(ordered
            ? ordered.condition
            : null);
    }, [ordering]);

    const handleSort = () => {
        let direction: Direction = 'asc';

        if (ordered === 'asc')
            direction = 'desc';

        applyOrdering([{ field: sortField, condition: direction }]);
    }

    const handleFilter = () => {
        setDisplay(true);
    }

    const modal = display
        ? (
            <MatchFilterModal title={title} field={field} options={options} onClose={() => setDisplay(false)} relatedFilter={relatedFilter} />
        )
        : null;

    return (
        <>
            <FilterHeader filtered={filtered} sortEnabled={sortEnabled} sorted={ordered} onFilter={handleFilter} onSort={handleSort} automationId={automationId}>{title}</FilterHeader>
            {modal}
        </>
    );
}

interface MatchFilterModalProps {
    title: string;
    field: string;
    options: any[];
    onClose: () => void;
    relatedFilter?: Filter;
}

export const MatchFilterModal = ({ title, field, options, onClose, relatedFilter }: MatchFilterModalProps) => {

    const [values, setValues] = useState<any[]>([]);
    const { filters, applyFilters, paging, applyPaging } = useQuery();

    useEffect(() => {
        const currentValues = filters
            .filter(f => f.field === field && f.condition === 'eq')
            .flatMap(f => f.values);

        setValues(currentValues);
    }, []);

    const handleApply = () => {
        let newFilters = filters.filter(f => f.field !== field);

        if (values && values.length > 0) {
            newFilters = [...newFilters, {
                field: field,
                condition: 'eq',
                values: values,
            }];

            if (relatedFilter)
                newFilters = [...newFilters, relatedFilter];
        }

        applyPaging({ ...paging, page: 1 });
        applyFilters(newFilters);
        onClose();
    }

    const handleClear = () => {

        applyPaging({ ...paging, page: 1 });
        applyFilters(filters.filter(f => f.field !== field));
        onClose();
    }

    return (
        <Modal header={`${title} Filter`} automationId="MatchSortHeader" size={ModalSize.Normal} show={true} closeModalHandler={onClose}>
            <div className="modal-body">
                <label>{title}</label>
                <Select
                    id="filter-match"
                    name="match"
                    isMulti={true}
                    classNamePrefix="multiSelect"
                    options={options}
                    autoFocus={true}
                    value={values.map(v => options.find(o => o.value === v))}
                    getOptionLabel={o => o.label}
                    getOptionValue={o => o.value}
                    onChange={(o: any[]) => setValues(o.map(v => v.value))}
                />
            </div>
            <div className="modal-footer">
                <button className="btn btn-primary" onClick={handleApply} data-automationid="submit-filter">OK</button>
                <button className="btn btn-danger" onClick={handleClear} data-automationid="clear-filter">Clear</button>
            </div>
        </Modal>
    );
};
